import { useEffect, useMemo, useState } from 'react';
import { PeriodicoArticle } from '@/lib/types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Image from '@/components/Image';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { normalizeMarkdown } from '@/components/ui/rich-text';
import { Loader2, BookOpen, Search, Newspaper, X } from 'lucide-react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { sanitizeFirestoreData, cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';

function ArticleReader({ article, children }: { article: PeriodicoArticle; children: React.ReactNode }) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border-white/10 p-0">
        <ScrollArea className="h-full">
          <article>
            <header className="relative aspect-video w-full">
              <Image src={article.imageUrl} alt={article.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-primary">{article.category}</p>
                <DialogTitle className="mt-2 font-headline text-2xl font-bold leading-tight text-white drop-shadow-lg md:text-3xl">
                  {article.title}
                </DialogTitle>
              </div>
            </header>
            <div className="p-6 md:p-8">
              <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  Por <strong className="text-foreground">{article.authorName}</strong>
                </span>
                <span className="text-primary/50">•</span>
                <span>{format(new Date(article.createdAt), "d 'de' MMMM, yyyy", { locale: es })}</span>
              </div>
              <div
                className={cn(
                  'prose prose-invert max-w-none text-muted-foreground prose-headings:font-headline prose-headings:text-foreground [column-gap:2.5rem]',
                  article.columns === 3 ? 'md:columns-3' : article.columns === 2 ? 'md:columns-2' : '',
                )}
              >
                <ReactMarkdown remarkPlugins={[remarkBreaks]}>{normalizeMarkdown(article.content)}</ReactMarkdown>
              </div>
            </div>
          </article>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function ArticleCard({ article, isFeatured = false, index = 0 }: { article: PeriodicoArticle; isFeatured?: boolean; index?: number }) {
  const previewContent = article.content.split('\n')[0] || article.subtitle;

  if (isFeatured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="group relative col-span-full overflow-hidden rounded-2xl border border-white/8 bg-card/40 backdrop-blur-sm"
      >
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500" />
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative aspect-video w-full overflow-hidden md:aspect-auto">
            <Image
              src={article.imageUrl}
              alt={article.title}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent md:bg-gradient-to-r" />
          </div>
          <div className="flex flex-col p-6 md:p-8">
            <span className="inline-flex w-fit items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-primary">
              {article.category}
            </span>
            <h2 className="mt-4 font-headline text-3xl font-bold leading-tight md:text-4xl">{article.title}</h2>
            <p className="mt-4 text-muted-foreground">{previewContent}</p>
            <div className="mt-auto flex items-center gap-2 pt-6 text-sm text-muted-foreground">
              <span>
                Por <strong className="text-foreground">{article.authorName}</strong>
              </span>
              <span className="text-primary/50">•</span>
              <span>{format(new Date(article.createdAt), "d 'de' MMMM, yyyy", { locale: es })}</span>
            </div>
            <ArticleReader article={article}>
              <Button className="mt-6 w-fit font-semibold">
                <BookOpen className="h-4 w-4" />
                Leer Noticia Completa
              </Button>
            </ArticleReader>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.05 }}
      whileHover={{ y: -6 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/8 bg-card/40 backdrop-blur-sm transition-colors hover:border-primary/30"
    >
      <div className="absolute inset-x-0 top-0 z-10 h-[3px] bg-gradient-to-r from-primary to-amber-500 opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <Image
          src={article.imageUrl}
          alt={article.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
      </div>
      <div className="flex flex-grow flex-col p-5">
        <span className="text-xs font-bold uppercase tracking-wider text-primary">{article.category}</span>
        <h3 className="mt-1.5 font-headline text-lg font-bold leading-snug">{article.title}</h3>
        <p className="mt-2 flex-grow text-sm text-muted-foreground line-clamp-3">{previewContent}</p>
        <div className="mt-4 text-xs text-muted-foreground">
          <span>
            Por <strong className="text-foreground">{article.authorName}</strong>
          </span>
          <span className="mx-1 text-primary/50">•</span>
          <span>{format(new Date(article.createdAt), "dd MMM, yyyy", { locale: es })}</span>
        </div>
        <ArticleReader article={article}>
          <Button variant="outline" size="sm" className="mt-4 w-full border-white/10 hover:border-primary/40 hover:bg-primary/10 hover:text-primary">
            Leer Noticia
          </Button>
        </ArticleReader>
      </div>
    </motion.div>
  );
}

export default function PeriodicoPage() {
  const [articles, setArticles] = useState<PeriodicoArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'periodico_articles'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const articlesData = snapshot.docs.map((doc) => sanitizeFirestoreData({ id: doc.id, ...doc.data() })) as PeriodicoArticle[];
        setArticles(articlesData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching articles:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(articles.map((a) => a.category).filter(Boolean));
    return Array.from(set);
  }, [articles]);

  const filteredArticles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return articles.filter((article) => {
      const matchesCategory = !activeCategory || article.category === activeCategory;
      if (!matchesCategory) return false;
      if (!term) return true;
      return (
        article.title?.toLowerCase().includes(term) ||
        article.subtitle?.toLowerCase().includes(term) ||
        article.content?.toLowerCase().includes(term) ||
        article.authorName?.toLowerCase().includes(term) ||
        article.category?.toLowerCase().includes(term)
      );
    });
  }, [articles, searchTerm, activeCategory]);

  const isFiltering = searchTerm.trim().length > 0 || activeCategory !== null;
  const featuredArticle = !isFiltering && filteredArticles.length > 0 ? filteredArticles[0] : null;
  const otherArticles = featuredArticle ? filteredArticles.slice(1) : filteredArticles;

  return (
    <div className="min-h-screen bg-background">
      <header className="relative overflow-hidden border-b border-white/8 py-20 text-center">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
        <div className="container relative mx-auto px-4">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10">
            <Newspaper className="h-7 w-7 text-primary" />
          </div>
          <h1 className="font-headline text-5xl font-extrabold tracking-tight md:text-7xl">
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">El Diario de Jartiland</span>
          </h1>
          <p className="mt-3 text-sm font-semibold uppercase tracking-[0.3em] text-muted-foreground">Crónicas de Jartiland</p>

          <div className="mx-auto mt-8 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar noticias por título, autor o contenido..."
                className="w-full rounded-2xl border border-white/10 bg-card/60 py-3 pl-11 pr-10 text-sm text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-colors focus:border-primary/50 focus:outline-none"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-primary"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {categories.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <button
                  onClick={() => setActiveCategory(null)}
                  className={cn(
                    'rounded-full border px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors',
                    activeCategory === null
                      ? 'border-primary/50 bg-primary/15 text-primary'
                      : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
                  )}
                >
                  Todas
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={cn(
                      'rounded-full border px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors',
                      activeCategory === cat
                        ? 'border-primary/50 bg-primary/15 text-primary'
                        : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
                    )}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
          </div>
        ) : articles.length === 0 ? (
          <div className="py-20 text-center">
            <h2 className="text-2xl font-semibold">No hay artículos publicados</h2>
            <p className="mt-2 text-muted-foreground">La rotativa está parada. Vuelve más tarde.</p>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="py-20 text-center">
            <h2 className="text-2xl font-semibold">Sin resultados</h2>
            <p className="mt-2 text-muted-foreground">No se ha encontrado ninguna noticia que coincida con tu búsqueda.</p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {featuredArticle && <ArticleCard article={featuredArticle} isFeatured />}
              {otherArticles.map((article, i) => (
                <ArticleCard key={article.id} article={article} index={i} />
              ))}
            </div>
          </AnimatePresence>
        )}
      </main>
    </div>
  );
}
